import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import {
  Calendar,
  Search,
  Download,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  QrCode,
} from "lucide-react";
import { scansApi } from "../utils/api";
import { ScanItem } from "../types";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

export function ScanLogs() {
  const [qrCodeFilter, setQrCodeFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [exporting, setExporting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [scans, setScans] = useState<ScanItem[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // Build ISO datetime range for the API
  const { startDateParam, endDateParam } = useMemo(() => {
    // Guard invalid ranges
    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      return {
        startDateParam: undefined as string | undefined,
        endDateParam: undefined as string | undefined,
      };
    }

    const toISOStartOfDay = (d: string) => {
      const date = new Date(d);
      date.setHours(0, 0, 0, 0);
      return date.toISOString();
    };

    const toISOEndOfDay = (d: string) => {
      const date = new Date(d);
      date.setHours(23, 59, 59, 999);
      return date.toISOString();
    };

    return {
      startDateParam: startDate ? toISOStartOfDay(startDate) : undefined,
      endDateParam: endDate ? toISOEndOfDay(endDate) : undefined,
    };
  }, [startDate, endDate]);

  const fetchScans = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.error("No authentication token found");
        setScans([]);
        setTotalPages(0);
        setTotal(0);
        setLoading(false);
        return;
      }

      const response = await scansApi.getAllScans(
        currentPage,
        limit,
        qrCodeFilter,
        startDateParam,
        endDateParam
      );

      if (response.data && response.data.scans && response.data.pagination) {
        const scansData = response.data.scans as ScanItem[];
        const pagination = response.data.pagination;
        setScans(scansData);
        setTotalPages(pagination.totalPages);
        setTotal(pagination.total);
      } else {
        console.error("Invalid API response structure for scans");
        setScans([]);
        setTotalPages(0);
        setTotal(0);
      }
    } catch (error) {
      console.error("Error fetching scans:", error);
      setScans([]);
      setTotalPages(0);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchScans();
  }, [currentPage, qrCodeFilter, startDateParam, endDateParam, limit]);

  const handleSearch = (value: string) => {
    setQrCodeFilter(value);
    setCurrentPage(1);
  };

  const handleStartDate = (value: string) => {
    setStartDate(value);
    setCurrentPage(1);
  };

  const handleEndDate = (value: string) => {
    setEndDate(value);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Parse backend scannedAt: expected format "DD/MM/YYYY, hh:mm am/pm"
  const parseScannedAt = (value: string): Date | null => {
    if (!value) return null;
    try {
      const [datePart, timePartRaw] = value.split(",").map((s) => s.trim());
      const [dd, mm, yyyy] = datePart.split("/").map(Number);
      const [timePart, ampm] = timePartRaw.split(" ").map((s) => s.trim());
      const [hhStr, minStr] = timePart.split(":");
      let hours = Number(hhStr);
      const minutes = Number(minStr);
      if (ampm.toLowerCase() === "pm" && hours < 12) hours += 12;
      if (ampm.toLowerCase() === "am" && hours === 12) hours = 0;
      const d = new Date(yyyy, mm - 1, dd, hours, minutes, 0, 0);
      if (isNaN(d.getTime())) return null;
      return d;
    } catch {
      return null;
    }
  };

  const startBoundary = useMemo(() => {
    if (!startDate) return null;
    const d = new Date(startDate);
    d.setHours(0, 0, 0, 0);
    return d;
  }, [startDate]);

  const endBoundary = useMemo(() => {
    if (!endDate) return null;
    const d = new Date(endDate);
    d.setHours(23, 59, 59, 999);
    return d;
  }, [endDate]);

  // Locally filter by scannedAt to guarantee correctness even if backend ignores filters
  const filteredScans = useMemo(() => {
    if (!startBoundary && !endBoundary) return scans;
    return scans.filter((s) => {
      const scannedDate = parseScannedAt(s.scannedAt);
      if (!scannedDate) return false;
      if (startBoundary && scannedDate < startBoundary) return false;
      if (endBoundary && scannedDate > endBoundary) return false;
      return true;
    });
  }, [scans, startBoundary, endBoundary]);

  const handleExportCSV = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      return;
    }
    setExporting(true);
    try {
      const response = await scansApi.getScanDataByDateAndQrId({
        startDate,
        endDate,
        qr_code_id: qrCodeFilter,
      });

      if (response.data && response.data.scanData) {
        const scanData = response.data.scanData;

        // Create CSV headers from the first object's keys.
        const headers = [
          "id",
          "qr_code_id",
          "user_id",
          "device",
          "ip_address",
          "is_active",
          "scanned_at",
          "latitude",
          "longitude",
        ];

        // Map data to CSV
        const rows = scanData.map((item: any) => [
          item.id,
          item.qr_code_id,
          item.user_id,
          item.device,
          item.ip_address,
          item.is_active,
          item.scanned_at,
          item.location?.coordinates?.[1], // latitude
          item.location?.coordinates?.[0], // longitude
        ]);

        // Join headers and rows
        const csvContent = [
          headers.join(","),
          ...rows.map((row: any) =>
            row
              .map(
                (val: any) =>
                  `"${
                    val !== undefined && val !== null
                      ? String(val).replace(/"/g, '""')
                      : ""
                  }"`
              )
              .join(",")
          ),
        ].join("\n");

        // Trigger download
        const blob = new Blob([csvContent], {
          type: "text/csv;charset=utf-8;",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "scan_data.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        setExporting(false);
      }
    } catch (error) {
      setExporting(false);
    }
  };

  const renderPagination = () => {
    const pages: React.ReactNode[] = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    if (startPage > 1) {
      pages.push(
        <Button
          key="first"
          variant="outline"
          size="sm"
          onClick={() => handlePageChange(1)}
          disabled={currentPage === 1}
        >
          <ChevronsLeft className="w-4 h-4" />
        </Button>
      );
    }

    pages.push(
      <Button
        key="prev"
        variant="outline"
        size="sm"
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
      >
        <ChevronLeft className="w-4 h-4" />
      </Button>
    );

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <Button
          key={i}
          variant={currentPage === i ? "default" : "outline"}
          size="sm"
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Button>
      );
    }

    pages.push(
      <Button
        key="next"
        variant="outline"
        size="sm"
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
      >
        <ChevronRight className="w-4 h-4" />
      </Button>
    );

    if (endPage < totalPages) {
      pages.push(
        <Button
          key="last"
          variant="outline"
          size="sm"
          onClick={() => handlePageChange(totalPages)}
          disabled={currentPage === totalPages}
        >
          <ChevronsRight className="w-4 h-4" />
        </Button>
      );
    }

    return pages;
  };

  const formatLocation = (loc?: { coordinates: number[] }) => {
    if (!loc || !loc.coordinates || loc.coordinates.length < 2) return "—";
    const [lat, lng] = loc.coordinates;
    return `${lat}, ${lng}`;
  };

  const handleLimitChange = (value: string) => {
    setLimit(parseInt(value, 10));
    setCurrentPage(1);
  };

  return (
    <div className="space-y-4 lg:space-y-6">
      {/* Loader Overlay */}
      {exporting && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="w-16 h-16 border-4 border-gray-200 border-t-black rounded-full animate-spin"></div>
        </div>
      )}
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold mb-2">Scan Logs</h1>
        <p className="text-sm lg:text-base text-muted-foreground">
          Track all QR code scans and user activity
        </p>
      </div>
      <Card className="shadow-sm">
        <CardContent className="pt-4 lg:pt-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
            <div>
              <label
                className="block text-sm mb-2"
                style={{ color: "var(--foreground)" }}
              >
                QR Unique ID
              </label>
              <div className="relative">
                <Search
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4"
                  style={{ color: "var(--muted-foreground)" }}
                />
                <Input
                  placeholder="Search QR Unique ID..."
                  value={qrCodeFilter}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="pl-10 text-sm lg:text-base"
                />
              </div>
            </div>
            <div>
              <label
                className="block text-sm mb-2"
                style={{ color: "var(--foreground)" }}
              >
                Start Date
              </label>
              <div className="relative">
                <Calendar
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4"
                  style={{ color: "var(--muted-foreground)" }}
                />
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => handleStartDate(e.target.value)}
                  className="pl-10 text-sm lg:text-base"
                />
              </div>
            </div>
            <div>
              <label
                className="block text-sm mb-2"
                style={{ color: "var(--foreground)" }}
              >
                End Date
              </label>
              <div className="relative">
                <Calendar
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4"
                  style={{ color: "var(--muted-foreground)" }}
                />
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => handleEndDate(e.target.value)}
                  className="pl-10 text-sm lg:text-base"
                />
              </div>
            </div>
            <Button
              className="flex items-center gap-2 h-10"
              onClick={handleExportCSV}
              disabled={loading || !startDate || !endDate}
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">Export CSV</span>
              <span className="sm:hidden">Export</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-sm overflow-visible">
        <CardHeader className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center justify-between">
          <CardTitle
            className="text-lg lg:text-xl"
            style={{ color: "var(--foreground)" }}
          >
            Scan History ({total} total)
          </CardTitle>

          <div>
            {/* Limit selector */}
            <Select value={limit.toString()} onValueChange={handleLimitChange}>
              <SelectTrigger className="w-full sm:w-32">
                <SelectValue placeholder="Items per page" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>

        <CardContent>
          {loading ? (
            <div className="flex justify-center items-center py-8">
              <div className="text-muted-foreground">Loading scans...</div>
            </div>
          ) : filteredScans.length === 0 ? (
            <div className="flex justify-center items-center py-8">
              <div className="text-muted-foreground">No scans found</div>
            </div>
          ) : (
            <>
              <div className="hidden lg:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead style={{ color: "var(--muted-foreground)" }}>
                        Date/Time
                      </TableHead>
                      <TableHead style={{ color: "var(--muted-foreground)" }}>
                        QR Code ID
                      </TableHead>
                      <TableHead style={{ color: "var(--muted-foreground)" }}>
                        QR Unique ID
                      </TableHead>
                      <TableHead style={{ color: "var(--muted-foreground)" }}>
                        Location
                      </TableHead>
                      <TableHead style={{ color: "var(--muted-foreground)" }}>
                        User
                      </TableHead>
                      <TableHead style={{ color: "var(--muted-foreground)" }}>
                        Device
                      </TableHead>
                      <TableHead style={{ color: "var(--muted-foreground)" }}>
                        IP Address
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredScans.map((log) => (
                      <TableRow key={log.id} className="hover:bg-muted/50">
                        <TableCell
                          className="font-mono text-sm"
                          style={{ color: "var(--foreground)" }}
                        >
                          {log.scannedAt}
                        </TableCell>
                        <TableCell
                          className="font-medium text-sm"
                          style={{ color: "var(--foreground)" }}
                        >
                          {log.qrCode?.id}
                        </TableCell>
                        <TableCell
                          className="font-mono text-sm"
                          style={{ color: "var(--foreground)" }}
                        >
                          {log.qrCode?.qrid}
                        </TableCell>
                        <TableCell
                          className="text-sm"
                          style={{ color: "var(--foreground)" }}
                        >
                          {formatLocation(log.location)}
                        </TableCell>
                        <TableCell
                          className="text-sm"
                          style={{ color: "var(--foreground)" }}
                        >
                          {log.user?.name} ({log.user?.email})
                        </TableCell>
                        <TableCell
                          className="text-sm"
                          style={{ color: "var(--foreground)" }}
                        >
                          {log.device}
                        </TableCell>
                        <TableCell
                          className="font-mono text-sm"
                          style={{ color: "var(--foreground)" }}
                        >
                          {log.ipAddress}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="lg:hidden space-y-3">
                {filteredScans.map((log) => (
                  <div
                    key={log.id}
                    className="border rounded-lg p-4 space-y-3 hover:bg-muted/50"
                    style={{ borderColor: "var(--border)" }}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1 min-w-0">
                        <h3
                          className="font-medium truncate"
                          style={{ color: "var(--foreground)" }}
                        >
                          {log.qrCode?.id}
                        </h3>
                        <p
                          className="text-sm truncate"
                          style={{ color: "var(--muted-foreground)" }}
                        >
                          {log.user?.name} ({log.user?.email})
                        </p>
                      </div>
                      <span
                        className="text-xs px-2 py-1 rounded ml-2"
                        style={{
                          color: "var(--muted-foreground)",
                          backgroundColor: "var(--muted)",
                        }}
                      >
                        {log.scannedAt.split(" ")[0]}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span style={{ color: "var(--muted-foreground)" }}>
                          Time:
                        </span>
                        <p
                          className="font-mono"
                          style={{ color: "var(--foreground)" }}
                        >
                          {log.scannedAt.split(" ")[1] || ""}
                        </p>
                      </div>
                      <div>
                        <span style={{ color: "var(--muted-foreground)" }}>
                          Location:
                        </span>
                        <p style={{ color: "var(--foreground)" }}>
                          {formatLocation(log.location)}
                        </p>
                      </div>
                      <div>
                        <span style={{ color: "var(--muted-foreground)" }}>
                          Device:
                        </span>
                        <p style={{ color: "var(--foreground)" }}>
                          {log.device}
                        </p>
                      </div>
                      <div>
                        <span style={{ color: "var(--muted-foreground)" }}>
                          IP:
                        </span>
                        <p
                          className="font-mono"
                          style={{ color: "var(--foreground)" }}
                        >
                          {log.ipAddress}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-2 mt-6">
                  {renderPagination()}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
